<template>
  <v-form>
    <div v-if="error" class="mb-4">
      <v-alert type="error" variant="tonal" closable>
        {{ error }}
      </v-alert>
    </div>

    <!-- Scenario 1: New image upload -->
    <template v-if="imageEmpty && item.can_change_image">
      <v-file-input
        v-model="file"
        :accept="filetype"
        label="Bestand selecteren"
        :error-messages="fileErrors"
        @input="v$.file.$touch()"
        @blur="v$.file.$touch()"
      />
      <v-btn
        class="mt-2"
        :loading="isLoading"
        block
        bottom
        color="primary"
        @click="submit"
        >Opslaan</v-btn
      >
    </template>

    <!-- Scenario 2: Existing image view -->
    <template v-else>
      <div v-if="item.file" class="text-center mb-4">
        <v-img
          :src="item.file"
          contain
          max-height="300"
          class="clickable-image"
          @click="showLightbox = true"
        >
          <template #placeholder>
            <v-row class="fill-height ma-0" align="center" justify="center">
              <v-progress-circular
                indeterminate
                color="primary"
              ></v-progress-circular>
            </v-row>
          </template>
          <div class="image-zoom-hint">
            <v-icon size="small" color="white">mdi-magnify-plus</v-icon>
            <span class="ml-1">Klik om te vergroten</span>
          </div>
        </v-img>
      </div>
      <div v-else class="text-center mb-4 pa-4 bg-grey-lighten-4">
        <v-icon size="large" color="grey">mdi-image-off</v-icon>
        <div class="text-body-2 mt-2">Geen afbeelding gevonden</div>
      </div>

      <!-- Image Lightbox -->
      <ImageLightbox
        v-model="showLightbox"
        :image-url="item.file"
        :download-url="item.file_dl"
        :title="'Afbeelding'"
      />

      <v-btn
        v-if="item.file"
        block
        color="primary"
        variant="elevated"
        class="mt-4 mb-2"
        :href="item.file_dl"
        prepend-icon="mdi-download"
      >
        Foto downloaden
      </v-btn>
      <v-btn
        v-if="!imageEmpty && item.can_change_image && item.id"
        block
        color="error"
        variant="outlined"
        prepend-icon="mdi-delete"
        @click="deleteImage()"
      >
        Foto verwijderen
      </v-btn>
      <v-btn
        v-if="imageEmpty && item.can_change_image"
        block
        color="primary"
        variant="outlined"
        prepend-icon="mdi-upload"
        class="mt-2"
        @click="showUploadForm = true"
      >
        Nieuwe foto uploaden
      </v-btn>

      <div v-if="showUploadForm" class="mt-4">
        <v-file-input
          v-model="file"
          :accept="filetype"
          label="Bestand selecteren"
          :error-messages="fileErrors"
          @input="v$.file.$touch()"
          @blur="v$.file.$touch()"
        />
        <v-btn
          class="mt-2"
          :loading="isLoading"
          block
          bottom
          color="primary"
          @click="submit"
          >Opslaan</v-btn
        >
      </div>
    </template>
  </v-form>
</template>

<script>
import { required } from "@vuelidate/validators";
import { useVuelidate } from "@vuelidate/core";
import { useFormsStore } from "@/stores/formsStore";
import { useAttachmentsStoreRefactor } from "@/stores/attachmentsStoreRefactor";
import { ref, computed, onMounted } from "vue";
import ImageLightbox from "./ImageLightbox.vue";

export default {
  name: "ImageUpload",
  components: {
    ImageLightbox,
  },
  props: {
    item: {
      type: Object,
      required: true,
      description:
        "The file item with id, file, file_dl, and can_change_image properties",
    },
    filetype: {
      type: String,
      default: "image/png, image/jpeg",
      description: "Accepted file types",
    },
    recordType: {
      type: String,
      required: true,
      description:
        'The type of record being operated on (e.g., "Account", "DayReport")',
    },
    recordId: {
      type: [Number, String],
      required: true,
      description: "The ID of the parent record",
    },
    field: {
      type: String,
      required: true,
      description:
        'The field name for the attachment (e.g., "profile_image_as", "attachments")',
    },
    onUploadComplete: {
      type: Function,
      default: () => {},
      description:
        "Callback function called after successful upload or deletion",
    },
  },
  setup(props) {
    const formsStore = useFormsStore();
    const attachmentsStore = useAttachmentsStoreRefactor();
    const isLoading = ref(false);
    const file = ref(null);
    const error = ref(null);
    const showUploadForm = ref(false);
    const showLightbox = ref(false);

    onMounted(() => {
      // Log props for debugging
      console.log("ImageUpload props:", {
        item: props.item,
        recordType: props.recordType,
        recordId: props.recordId,
        field: props.field,
      });

      // Additional debug info for image detection
      console.log("Image status:", {
        imageEmpty: !props.item.file || props.item.file === "",
        hasID: !!props.item.id,
        canChange: !!props.item.can_change_image,
        fileUrl: props.item.file,
      });

      if (!props.recordType || !props.recordId || !props.field) {
        error.value = "Ontbrekende vereiste eigenschappen voor bestandsupload";
      }
    });

    const rules = {
      file: { required },
    };

    const v$ = useVuelidate(rules, { file });

    const imageEmpty = computed(() => {
      // Check for null/undefined/empty string
      return !props.item.file || props.item.file === "";
    });

    const fileErrors = computed(() => {
      const errors = [];
      if (!v$.value.file.$dirty) return errors;
      !v$.value.file.required && errors.push("Bestand is verplicht");
      return errors;
    });

    const deleteImage = async () => {
      isLoading.value = true;
      error.value = null;

      try {
        if (!props.item.id) {
          throw new Error("Geen bijlage ID gevonden");
        }

        await attachmentsStore.delete(props.item.id);
        isLoading.value = false;
        formsStore.$reset();

        if (typeof props.onUploadComplete === "function") {
          props.onUploadComplete();
        }
      } catch (err) {
        error.value = err.message || "Kon de afbeelding niet verwijderen";
        console.error("Error deleting image:", err);
        isLoading.value = false;
      }
    };

    const submit = async () => {
      v$.value.$touch();
      isLoading.value = true;
      error.value = null;

      if (v$.value.$invalid) {
        isLoading.value = false;
        return;
      }

      try {
        // Validate required props
        if (!props.recordType || !props.recordId || !props.field) {
          throw new Error(
            "Ontbrekende vereiste eigenschappen voor bestandsupload",
          );
        }

        // Get the upload URL from our API (this needs auth)
        console.log("Step 1: Getting upload URL from API...");
        let response;
        try {
          response = await attachmentsStore.getUploadUrl();
          console.log("Upload URL response:", response);
        } catch (uploadUrlError) {
          console.error("Failed to get upload URL:", uploadUrlError);
          throw new Error(
            `Failed to get upload URL: ${uploadUrlError.message || uploadUrlError}`,
          );
        }

        // Validate the response
        if (!response || !response.url || !response.url_fields) {
          console.error("Invalid upload URL response:", response);
          throw new Error("Invalid upload URL response from server");
        }

        // Test basic connectivity to S3 domain
        const s3Domain = new URL(response.url).origin;
        console.log("Step 2: Testing connectivity to S3 domain:", s3Domain);

        try {
          await fetch(s3Domain, {
            method: "HEAD",
            mode: "no-cors",
            signal: AbortSignal.timeout(5000),
          });
          console.log("S3 domain connectivity test passed");
        } catch (connectivityError) {
          console.warn(
            "S3 domain connectivity test failed:",
            connectivityError.message,
          );
          // Continue anyway, as no-cors mode might not give accurate results
        }

        const formData = new FormData();

        // Add all the S3 fields first
        for (const key in response.url_fields) {
          formData.append(key, response.url_fields[key]);
        }

        // Add the file last
        formData.append("file", file.value);

        console.log("Step 3: Uploading to S3...");
        console.log("S3 URL:", response.url);
        console.log("File info:", {
          name: file.value.name,
          size: file.value.size,
          type: file.value.type,
        });
        console.log("FormData fields:", Object.fromEntries(formData.entries()));

        // Use fetch for the S3 upload with better error handling
        let uploadResponse;
        try {
          // Add timeout for the S3 upload
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

          uploadResponse = await fetch(response.url, {
            method: "POST",
            body: formData,
            // Add mode and credentials for CORS handling
            mode: "cors",
            credentials: "omit",
            signal: controller.signal,
          });

          clearTimeout(timeoutId);
        } catch (fetchError) {
          if (fetchError.name === "AbortError") {
            throw new Error(
              "Upload timeout - de verbinding met S3 duurde te lang",
            );
          } else if (
            fetchError.message.includes("Failed to fetch") ||
            fetchError.message.includes("NetworkError")
          ) {
            throw new Error(
              "Netwerkfout - kan geen verbinding maken met S3. Controleer je internetverbinding.",
            );
          } else {
            throw new Error(`Netwerkfout bij S3 upload: ${fetchError.message}`);
          }
        }

        console.log("S3 upload response status:", uploadResponse.status);
        console.log(
          "S3 upload response headers:",
          Object.fromEntries(uploadResponse.headers.entries()),
        );

        const uploadData = await uploadResponse.text();
        console.log("S3 upload response data:", uploadData);

        // Check if the response was successful
        if (!uploadResponse.ok) {
          throw new Error(
            `S3 upload failed with status ${uploadResponse.status}: ${uploadResponse.statusText}`,
          );
        }

        // Check if we got an error response in the XML
        if (uploadData.includes("<Error>") || uploadData.includes("<e>")) {
          const parser = new DOMParser();
          const errorDoc = parser.parseFromString(uploadData, "text/xml");
          const errorMessage =
            errorDoc.getElementsByTagName("Message")[0]?.childNodes[0]
              ?.nodeValue ||
            errorDoc.getElementsByTagName("message")[0]?.childNodes[0]
              ?.nodeValue;
          const errorCode =
            errorDoc.getElementsByTagName("Code")[0]?.childNodes[0]
              ?.nodeValue ||
            errorDoc.getElementsByTagName("code")[0]?.childNodes[0]?.nodeValue;
          throw new Error(`S3 Error ${errorCode}: ${errorMessage}`);
        }

        const parser = new DOMParser();
        const doc = parser.parseFromString(uploadData, "text/xml");

        const s3Response = {
          key: doc.getElementsByTagName("Key")[0]?.childNodes[0]?.nodeValue,
          md5: doc.getElementsByTagName("ETag")[0]?.childNodes[0]?.nodeValue,
          file_name: file.value.name,
          file_size: file.value.size,
          file_type: file.value.type,
        };

        // Validate that we got all the required fields
        if (!s3Response.key || !s3Response.md5) {
          throw new Error("Kon S3 reactie niet verwerken");
        }

        // Send to the generic attachments endpoint
        console.log("Step 4: Saving attachment record to database...");
        try {
          await attachmentsStore.createAttachment(
            props.recordType,
            props.recordId,
            props.field,
            s3Response,
          );
          console.log("Step 4: Attachment record saved successfully");
        } catch (attachmentSaveError) {
          console.error(
            "Failed to save attachment record:",
            attachmentSaveError,
          );

          // Check for specific server errors
          if (
            typeof attachmentSaveError === "string" &&
            attachmentSaveError.includes("redis")
          ) {
            throw new Error(
              "Server configuratiefout: Redis database niet beschikbaar. Neem contact op met de beheerder.",
            );
          } else if (
            typeof attachmentSaveError === "string" &&
            attachmentSaveError.includes("Connection refused")
          ) {
            throw new Error(
              "Server database fout. Het bestand is geüpload naar S3, maar de database record kon niet worden opgeslagen. Neem contact op met de beheerder.",
            );
          } else {
            throw new Error(
              `Er is een fout opgetreden bij het uploaden van het bestand: ${attachmentSaveError}`,
            );
          }
        }

        isLoading.value = false;
        formsStore.$reset();

        if (typeof props.onUploadComplete === "function") {
          props.onUploadComplete();
        }
      } catch (err) {
        error.value = err.message || "Kon bestand niet uploaden";
        console.error("Error uploading file:", err);
        console.error("Error details:", {
          message: err.message,
          name: err.name,
          stack: err.stack,
          response: err.response,
          status: err.status,
          statusText: err.statusText,
          code: err.code,
        });

        // Add specific troubleshooting for production issues
        const errorMessage = err.message || err.toString() || "Unknown error";
        if (
          errorMessage.includes("NetworkError") ||
          errorMessage.includes("Failed to fetch")
        ) {
          error.value =
            "Netwerkfout bij het uploaden. Dit kan komen door:\n• Firewall instellingen\n• CORS configuratie\n• Internetverbinding\n\nProbeer het opnieuw of neem contact op met de beheerder.";
        } else if (errorMessage.includes("timeout")) {
          error.value =
            "Upload timeout - het bestand is te groot of de verbinding te langzaam. Probeer een kleiner bestand of betere internetverbinding.";
        } else if (errorMessage.includes("S3 Error")) {
          error.value = `S3 server fout: ${errorMessage}\n\nDit is waarschijnlijk een configuratieprobleem. Neem contact op met de beheerder.`;
        } else if (errorMessage.includes("Failed to get upload URL")) {
          error.value =
            "Kon geen upload URL verkrijgen van de server. Controleer je internetverbinding en probeer opnieuw.";
        } else if (errorMessage.includes("Invalid upload URL response")) {
          error.value =
            "Ongeldige reactie van de server. Dit is waarschijnlijk een server configuratieprobleem.";
        } else if (
          errorMessage.includes("Redis") ||
          errorMessage.includes("redis")
        ) {
          error.value =
            "Server database fout (Redis). Het bestand kan zijn geüpload, maar de database record werd niet opgeslagen. Neem contact op met de beheerder.";
        } else if (
          errorMessage.includes("Connection refused") ||
          errorMessage.includes("database")
        ) {
          error.value =
            "Server database fout. Het bestand is mogelijk geüpload, maar de database record kon niet worden opgeslagen. Neem contact op met de beheerder.";
        }

        isLoading.value = false;
      }
    };

    return {
      file,
      isLoading,
      error,
      v$,
      imageEmpty,
      fileErrors,
      deleteImage,
      submit,
      showUploadForm,
      showLightbox,
    };
  },
};
</script>

<style scoped>
.v-alert {
  margin-bottom: 16px;
}

.clickable-image {
  cursor: pointer;
  position: relative;
  transition: transform 0.2s ease;
}

.clickable-image:hover {
  transform: scale(1.02);
}

.image-zoom-hint {
  position: absolute;
  bottom: 8px;
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(0, 0, 0, 0.6);
  color: white;
  padding: 4px 12px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  font-size: 0.75rem;
  opacity: 0;
  transition: opacity 0.2s ease;
  pointer-events: none;
}

.clickable-image:hover .image-zoom-hint {
  opacity: 1;
}
</style>
